import { useState, useRef, MouseEventHandler } from 'react'
import { Button, Heading, Separator } from '@radix-ui/themes'
import { Flex, Text, Card, Box } from '@radix-ui/themes'
import { FileIcon } from '@radix-ui/react-icons'
import { createFileRoute } from '@tanstack/react-router'
import { LuArrowDown, LuCamera, LuFileCheck2, LuX } from 'react-icons/lu'
import { Accept, useDropzone } from 'react-dropzone'
import bytes from 'bytes'
import classNames from 'classnames'
import { createScan } from './-service'
import { useTRPCClient } from '~/utils/trpc'

export const Route = createFileRoute('/plugins/eiffage-scans/new')({
  component: RouteComponent
})

const MAX_UPLOAD_SIZE = bytes('10MB') as number
const ACCEPTED_FILES: Accept = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/svg+xml': ['.svg'],
  'image/png': ['.png'],
  'image/webp': ['.webp']
}

function RouteComponent() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const { org } = Route.useRouteContext()
  const navigate = Route.useNavigate()

  const [error, setError] = useState<string>('')

  const TRPCClient = useTRPCClient()
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: false,
    accept: ACCEPTED_FILES,
    maxSize: MAX_UPLOAD_SIZE,
    onDropRejected: (rejectedFiles) => {
      setError(`${rejectedFiles.map((f) => f.file.name).join(', ')} not accepted.`)
    },
    onDrop: async ([file]) => {
      if (!file) {
        return
      }
      setPreviewUrl(URL.createObjectURL(file))
      setSelectedFile(file)
    }
  })

  const handleFileSelect = (file: File) => {
    setSelectedFile(file)
    const fileReader = new FileReader()
    fileReader.onload = (e) => {
      setPreviewUrl(e.target?.result as string)
    }
    fileReader.readAsDataURL(file)
  }

  const handleCameraClick: MouseEventHandler = (e) => {
    e.stopPropagation()
    cameraInputRef.current?.click()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0])
    }
  }

  //TODO create mutation from this
  const handleSendFile = async () => {
    if (!selectedFile) {
      return
    }
    const { scanID } = await createScan(org.id, selectedFile)
    void TRPCClient.plugins.scansRouter.processScan.mutate({ scanID })
    navigate({ to: '/plugins/eiffage-scans/$scanID', params: { scanID: `${scanID}` } })
  }

  return (
    <Flex align={'center'} justify={'center'} className={classNames('w-full h-full relative')} {...getRootProps()}>
      <div
        className={classNames('absolute h-[100%] w-[100%] bg-white bg-opacity-35 backdrop-blur-md z-30', {
          block: isDragActive,
          hidden: !isDragActive
        })}
      >
        <Flex direction={'column'} m={'auto'} align="center" justify="center" className="h-full">
          <LuArrowDown size={50} className="animate-bounce text-mint-8" />
          <Text size={'6'} weight={'medium'}>
            Déposez votre fichier
          </Text>
        </Flex>
      </div>
      <Flex direction="column" gap="6" mt={'-9'}>
        <Heading size={'8'} align={'center'}>
          Nouvelle numérisation de carnet
        </Heading>
        {previewUrl ? (
          <>
            <Card className="mb-5 relative">
              <img src={previewUrl} alt="Document preview" className="w-full max-h-[45vh] object-contain" />
              <Flex p="3" justify="between" align="center">
                <Box>
                  <Text weight="medium" as="p">
                    {selectedFile?.name || 'Selected document'}
                  </Text>
                  <Text size="1" color="gray">
                    {bytes(selectedFile?.size ?? 0, { unitSeparator: ' ' })}
                  </Text>
                </Box>
                <Button
                  variant="ghost"
                  color="gray"
                  onClick={() => {
                    setPreviewUrl(null)
                    setSelectedFile(null)
                  }}
                >
                  <LuX size={16} color="gray" />
                  Annuler
                </Button>
              </Flex>
            </Card>
            <Button size="4" onClick={handleSendFile} disabled={!selectedFile}>
              <LuFileCheck2 size={22} />
              Envoyer la numérisation
            </Button>
          </>
        ) : (
          <>
            <Flex
              p="4"
              align={'center'}
              justify={'center'}
              direction={'column'}
              className="border-2 border-dashed rounded-md w-full border-gray-5"
            >
              <FileIcon width={40} height={40} className="text-gray-500 mb-3" />
              <Text weight="medium">Téléverser un carnet de suivi</Text>
              <Text size="1" color="gray" className="mt-1 hidden sm:block text-center">
                Cliquez ou glissez-déposez un fichier
              </Text>
              <Text size="1" color="gray" className="mt-1 sm:hidden text-center">
                Appuyez pour sélectionner un document depuis votre appareil
              </Text>
              <input {...getInputProps()} />
            </Flex>
            {error ? (
              <Text size="1" color="red" align="center" mt={'-4'}>
                {error}
              </Text>
            ) : (
              <Text size="1" color="gray" align="center" mt={'-4'}>
                Formats supportés: JPG, PNG
              </Text>
            )}
            <Flex direction={'column'} gap={'6'} className="sm:hidden">
              <Flex gap={'2'} justify={'center'} align="center" className="w-full">
                <Separator size="1" className="my-2" />
                <Text size="1" color="gray" align="center">
                  ou
                </Text>
                <Separator size="1" className="my-2" />
              </Flex>

              {/* Camera/Gallery Options */}
              <Button size="4" onClick={handleCameraClick} variant="soft">
                <LuCamera width={16} height={16} />
                <Text>Prendre une photo</Text>
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </Button>
            </Flex>
          </>
        )}
      </Flex>
    </Flex>
  )
}
